import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { message, Table } from 'antd';
import * as HttpStatus from 'http-status-codes';
import PageLoader from '../../../../../../components/PageLoader/PageLoader';
import PlanProcessBar from '../../../../../../components/ProcessBar/PlanProcessBar';
import Card from '../../../../../../components/Card/Card';
import InfoBox from '../../../../../../components/InfoBox/InfoBox';
import ModuleEmptyState from '../../../../../../components/ModuleEmptyState';
import { fetchRiskExposures } from '../../RiskAction';
import {
  industryCodeSelector,
  loadingOrganisationSelector,
  riskWorkflowGatingSelector,
} from '../../../../../Organisation/OrganisationSelectors';
import { riskExposuresSelector } from '../../RiskSelectors';
import { planExpiryDateSelector } from '../../../Plan/PlanSelectors';
import { getPlanExpiryStatus } from '../../../../../../components/PlanExpiryBadge/plan-expiry-shared';
import { MSG_ERROR, MSG_PAYMENT_REQUIRED } from '../../../../../../constants';
import { getApiErrorMessage } from '../../../../../../utils/httpError';
import { getPartnerAccent } from '../../../../../../themes/partnerTheme';

const THEME_COLOR = getPartnerAccent('risk');
const INFO_MSG = (
  <>
    <p>
      This is a read only page to bring to your attention Risk Exposures identified as being considerations for
      Organisations operating in your industry.
    </p>
    <p>This is not a complete and exhaustive list and is intended as a general guide only.</p>
  </>
);

const RiskExposure = ({
  industryId,
  loadingOrganisation,
  planExpiry,
  riskExposures,
  fetchRiskExposures,
  isRiskWorkflowGatingEnabled,
}) => {
  const [fetchState, setFetchState] = useState('loading');
  const isPlanExpired = getPlanExpiryStatus(planExpiry) === 'expired';

  useEffect(() => {
    let active = true;

    async function loadExposures() {
      if (!industryId) {
        if (loadingOrganisation || !isPlanExpired) return;
        if (!active) return;
        message.error(MSG_PAYMENT_REQUIRED);
        setFetchState('payment_required');
        return;
      }

      setFetchState('loading');
      const result = await fetchRiskExposures(industryId);
      if (!active) return;
      if (result?.ok) {
        setFetchState('ready');
        return;
      }
      if (result?.status === HttpStatus.PAYMENT_REQUIRED) {
        message.error(MSG_PAYMENT_REQUIRED);
        setFetchState('payment_required');
        return;
      }
      message.error(getApiErrorMessage(result?.error, MSG_ERROR));
      setFetchState('error');
    }

    loadExposures();
    return () => {
      active = false;
    };
  }, [industryId, loadingOrganisation, isPlanExpired, fetchRiskExposures]);

  const columns = [{ key: 'name', dataIndex: 'name' }];

  return (
    <>
      <PlanProcessBar
        color={THEME_COLOR}
        title="Risk Exposures"
        disableNext={isRiskWorkflowGatingEnabled && !riskExposures}
      />
      <InfoBox
        color={THEME_COLOR}
        guidance={<><strong>What to do:</strong> review the industry-specific risk exposures below as a <strong>reference guide</strong> — this is not an exhaustive list. Use these as a starting point when completing your Risk Assessment on the next step.</>}
        message={INFO_MSG}
        more
        video={
          <iframe
            title="risk-exposure"
            src="https://www.youtube.com/embed/sa6yHXSWpew?rel=0&amp;showinfo=0"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            className="responsive-video"
          />
        }
      />
      {fetchState === 'loading' ? (
        <PageLoader tip="Loading Risk Exposures..." />
      ) : null}
      {fetchState === 'payment_required' ? (
        <ModuleEmptyState
          embedded
          moduleTitle="Risk Exposures"
          headline="Renewal required"
          description={MSG_PAYMENT_REQUIRED}
          iconType="warning"
        />
      ) : null}
      {fetchState === 'error' ? (
        <ModuleEmptyState
          embedded
          moduleTitle="Risk Exposures"
          headline="Unable to load risk exposures"
          description={MSG_ERROR}
          iconType="warning"
        />
      ) : null}
      {fetchState === 'ready' ? (
        <div className="risk-container">
          <Card color={THEME_COLOR} title="Risk Exposures">
            <Table
              columns={columns}
              dataSource={riskExposures || []}
              rowKey={record => record.id}
              pagination={{ hideOnSinglePage: true }}
              showHeader={false}
              className="table"
            />
          </Card>
        </div>
      ) : null}
    </>
  );
};

const mapStateToProps = state => ({
  industryId: industryCodeSelector(state),
  loadingOrganisation: loadingOrganisationSelector(state),
  planExpiry: planExpiryDateSelector(state),
  riskExposures: riskExposuresSelector(state),
  isRiskWorkflowGatingEnabled: riskWorkflowGatingSelector(state)
});

const mapDispatchToProps = { fetchRiskExposures };

export default connect(mapStateToProps, mapDispatchToProps)(RiskExposure);
